import cn from 'classnames';
import { useContext } from 'react';
import { useSelector } from 'react-redux';

import Banner, { ERROR_TYPE } from 'components/common/Banner';
import { CustomerReportContext } from 'components/grace/Contexts';
import { TEMPO } from 'context/BrandContext';
import { dashboardRootPath } from 'helpers/routes';
import useBrand from 'hooks/useBrand';
import tempoExpirationDate from 'images/grace/tempo-expiration-date.png';
import { STAGE_TYPES, WHAT_DO_YOU_NEED_HELP_WITH } from 'lib/constants';
import * as fromGrace from 'reducers/grace';

import GraceCredit from './Credit';
import GraceCreditAndRefund from './CreditAndRefund';
import GraceListRowButton from './ListRowButton';
import GraceRefund from './Refund';

const GraceContentHeader = () => {
  const { closeReport, currentStage, errorMessage, isUserLoaded } =
    useContext(CustomerReportContext);
  const isTempo = useBrand(TEMPO);
  const resolutionCredit = useSelector(state => fromGrace.selectResolution(state).resolutionCredit);
  const resolutionRefund = useSelector(state => fromGrace.selectResolution(state).resolutionRefund);
  const resolutionTotal = useSelector(state => fromGrace.selectResolution(state).resolutionTotal);

  const backToUpcomingOrdersOption = { id: 'back-to-orders', name: 'Go back to Upcoming Orders' };
  const displayFindExpirationContent = () => {
    return currentStage.slug === 'find-exp-date-tempo';
  };
  const headingTextStyle = () => {
    if (displayFindExpirationContent()) {
      return 'h4 whitespace-pre-wrap';
    } else {
      return 'h2 whitespace-pre-wrap';
    }
  };

  const headerSubtitle = () => {
    return (
      <section className="px-6 py-3">
        <p className="textSm py-2" dangerouslySetInnerHTML={{ __html: currentStage?.subtitle }}></p>
      </section>
    );
  };

  const backToUpcomingOrders = () => {
    closeReport();
    window.location.replace(dashboardRootPath());
  };

  const findExpirationContent = () => {
    return (
      <>
        <section className="flex justify-center">
          <img
            className="mx-auto pt-2"
            width="80%"
            alt="Find expiration date on sleeve"
            src={tempoExpirationDate}
          />
        </section>

        {headerSubtitle()}

        <section className="mx-4">
          <GraceListRowButton option={backToUpcomingOrdersOption} onClick={backToUpcomingOrders} />
        </section>
      </>
    );
  };

  const resolutionHeader = () => {
    return (
      <>
        <section
          className={cn(
            isTempo ? '!bg-tempo-gray-40' : '!bg-cream-100',
            resolutionCredit || resolutionRefund ? 'pb-10' : ''
          )}
        >
          {errorMessage && isUserLoaded && (
            <Banner className="p-2" type={ERROR_TYPE} subtitle={errorMessage} />
          )}
          <section className="p-6">
            <p className={headingTextStyle()} role="heading">
              {currentStage?.title || WHAT_DO_YOU_NEED_HELP_WITH}
            </p>
          </section>
        </section>
        {resolutionCredit && !resolutionRefund && <GraceCredit credit={resolutionCredit} />}
        {resolutionRefund && !resolutionCredit && <GraceRefund refund={resolutionRefund} />}
        {resolutionRefund && resolutionCredit && (
          <GraceCreditAndRefund
            total={resolutionTotal}
            credit={resolutionCredit}
            refund={resolutionRefund}
          />
        )}

        {displayFindExpirationContent() ? findExpirationContent() : headerSubtitle()}
      </>
    );
  };

  const header = () => {
    return (
      <>
        {errorMessage && isUserLoaded && (
          <Banner className="p-2" type={ERROR_TYPE} subtitle={errorMessage} />
        )}
        <section className="mb-6 px-6">
          <h2 className={'h2 whitespace-pre-wrap'}>
            {currentStage?.title || WHAT_DO_YOU_NEED_HELP_WITH}
          </h2>
          <p className="textSm mt-2">{currentStage?.subtitle || ''}</p>
        </section>
      </>
    );
  };

  return currentStage.stageType === STAGE_TYPES.resolution ? resolutionHeader() : header();
};

export default GraceContentHeader;
